/**
 * useBeforeUnloadGuard — warns the user before closing / reloading the
 * browser tab when an order is being placed, a draft save is in flight, or
 * local Zustand state is still dirty (autosave debounce window).
 *
 * Phase 4 introduced this guard on the Summary step only. Phase E-B.6 lifts
 * the inline `useEffect` to a shared hook and applies it to Instructions +
 * Billing as well — both steps now write directly to the native Zustand
 * store (no legacy provider tree, no state bridge), but the debounced
 * autosave still leaves a window where a keystroke hasn't been persisted
 * yet when the tab closes.
 *
 * State is read imperatively via `usePfStore.getState()` so the closure
 * stays fresh across re-renders without subscribing the hook to store
 * updates (the empty deps array is intentional — we want a single
 * registration per page mount).
 */

import { useEffect } from 'react'

import { usePfStore } from '../store'

/**
 * Registers a `beforeunload` handler for the lifetime of the calling
 * component. The handler short-circuits unless the draft slice reports
 * `saveStatus === 'saving'` or `dirty === true`.
 *
 * @example
 * export const Instructions: React.FC = () => {
 *   useBeforeUnloadGuard()
 *   // …
 * }
 */
export const useBeforeUnloadGuard = (): void => {
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const { saveStatus, dirty } = usePfStore.getState().draft
      // An order in flight is the costlier thing to interrupt: legacy warned on
      // it from `ValidationController`, which the Summary-native rewrite retired,
      // so the reason moved in here beside the autosave window.
      const { isPlacingOrder } = usePfStore.getState()
      if (isPlacingOrder || saveStatus === 'saving' || dirty) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
      return undefined
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])
}
